/**
 * mortgageCalcEngine.js
 * Pure math functions for mortgage calculations.
 * Extracted from MortgagePaymentCalculator.js + MortgageSummary.js
 */

/* ── Down payment rules (CMHC) ── */

export function calculateMinimumDownPayment(price) {
  if (price <= 500000) return price * 0.05;
  if (price <= 1499999) return 500000 * 0.05 + (price - 500000) * 0.1;
  return price * 0.2;
}

export function getMortgageInsuranceRate(dpPercent) {
  if (dpPercent >= 20) return 0;
  if (dpPercent >= 15) return 2.8;
  if (dpPercent >= 10) return 3.1;
  return 4.0;
}

/* ── Payment frequency helpers ── */

export function paymentsPerYearFromFreq(freq) {
  switch (freq) {
    case 'semi-monthly': return 24;
    case 'bi-weekly':
    case 'accelerated-bi-weekly': return 26;
    case 'weekly':
    case 'accelerated-weekly': return 52;
    default: return 12;
  }
}

export function convertPaymentFrequency(monthlyPayment, targetFreq) {
  switch (targetFreq) {
    case 'semi-monthly': return monthlyPayment / 2;
    case 'bi-weekly': return (monthlyPayment * 12) / 26;
    case 'accelerated-bi-weekly': return monthlyPayment / 2;
    case 'weekly': return (monthlyPayment * 12) / 52;
    case 'accelerated-weekly': return monthlyPayment / 4;
    default: return monthlyPayment;
  }
}

/* ── Core PMT calculation (semi-annual compounding — Canadian standard) ── */

export function calculateMortgage({
  homePrice,
  downPayment,
  annualRate,
  amortYears = 25,
  paymentsPerYear = 12,
  lumpSumAmount = 0,
  lumpSumYears = 0,
  extraPaymentPerPeriod = 0,
  loanTerm = 5,
}) {
  if (!homePrice || homePrice <= 0 || !amortYears || amortYears <= 0) {
    return { error: 'Invalid inputs' };
  }

  const principal = homePrice - downPayment;
  const dpPercent = (downPayment / homePrice) * 100;
  const insuranceRate = getMortgageInsuranceRate(dpPercent);
  const mortgageInsurance = principal * (insuranceRate / 100);
  const totalLoan = principal + mortgageInsurance;

  if (annualRate < 0) return { error: 'Rate must be positive' };

  // Canadian semi-annual compounding
  const effectiveAnnualRate = Math.pow(1 + annualRate / 2, 2) - 1;
  const ratePerPeriod = Math.pow(1 + effectiveAnnualRate, 1 / paymentsPerYear) - 1;
  const totalPayments = paymentsPerYear * amortYears;
  const paymentsInTerm = paymentsPerYear * loanTerm;

  // Base PMT
  const basePayment =
    (totalLoan * ratePerPeriod) / (1 - Math.pow(1 + ratePerPeriod, -totalPayments));

  const paymentPerPeriod = basePayment + extraPaymentPerPeriod;
  const lumpSumPeriod = lumpSumYears * paymentsPerYear;

  // Amortize to find actual payoff
  let balance = totalLoan;
  let totalInterest = 0;
  let termInterest = 0;
  let actualPayments = 0;

  for (let i = 0; i < totalPayments && balance > 0; i++) {
    const interest = balance * ratePerPeriod;
    totalInterest += interest;
    if (i < paymentsInTerm) termInterest += interest;
    const principalPaid = paymentPerPeriod - interest;
    balance -= principalPaid;
    if (balance < 0) balance = 0;

    if (lumpSumAmount > 0 && i === lumpSumPeriod) {
      balance -= lumpSumAmount;
      if (balance < 0) balance = 0;
    }

    actualPayments = i + 1;
    if (balance <= 0) break;
  }

  return {
    paymentPerPeriod,
    basePayment,
    principalAmount: principal,
    totalLoan,
    mortgageInsurance,
    cmhcRequired: dpPercent < 20,
    cmhcRate: insuranceRate,
    downPaymentPercent: dpPercent,
    totalPayments: actualPayments,
    numberOfPaymentsAmortization: totalPayments,
    numberOfPaymentsTerm: paymentsInTerm,
    ratePerPeriod,
    paymentsPerYear,
    effectiveAnnualRate,
    annualRate,
    homePrice,
    downPayment,
    amortYears,
    loanTerm,
    totalInterestAmort: totalInterest,
    totalInterestTerm: termInterest,
    totalCostAmort: totalLoan + totalInterest,
  };
}

/* ── Enrich with monthly/biweekly conversions ── */

export function enrichResult(result) {
  if (!result || result.error) return result;
  const monthly = result.paymentsPerYear === 12
    ? result.paymentPerPeriod
    : convertPaymentFrequency(result.paymentPerPeriod, 'monthly');
  return {
    ...result,
    monthlyPayment: result.paymentsPerYear === 12
      ? result.paymentPerPeriod
      : (result.totalLoan * result.ratePerPeriod) / (1 - Math.pow(1 + result.ratePerPeriod, -result.numberOfPaymentsAmortization)),
    biweeklyPayment: convertPaymentFrequency(monthly, 'bi-weekly'),
  };
}

/* ── Rate helpers for Mortgages.json data ── */

export function getLowestPromoRates(data, term, rateType, limit = 6) {
  const rates = [];
  data.banks.forEach((bank) => {
    const bankRates = bank.promoRates[rateType];
    if (bankRates) {
      bankRates.forEach((rate) => {
        if (rate.term === term && rate.rate) {
          rates.push({
            bankName: bank.name,
            bankLogo: bank.logo,
            term: rate.term,
            rate: rate.rate,
            lastChange: rate.lastChange,
            prepayments: rate.prepayments,
            LTV: rate.LTV,
          });
        }
      });
    }
  });
  rates.sort((a, b) => a.rate - b.rate);
  return rates.slice(0, limit);
}

export function getAverageLowest(data, term, rateType) {
  const rates = [];
  data.banks.forEach((bank) => {
    const bankRates = bank.promoRates[rateType];
    if (bankRates) {
      const match = bankRates.filter((r) => r.term === term && r.rate);
      if (match.length > 0) {
        const lowest = Math.min(...match.map((r) => r.rate));
        rates.push(lowest);
      }
    }
  });
  if (rates.length === 0) return '-';
  const avg = rates.reduce((a, b) => a + b, 0) / rates.length;
  return avg.toFixed(2);
}

/* ── Currency formatting ── */

export function formatCurrency(value) {
  return value.toLocaleString('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
  });
}

export function formatCurrencyShort(value) {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return formatCurrency(value);
}
